import { Card, cn } from "@breenova/ui";
import {
  BellRing,
  Boxes,
  CalendarRange,
  ShieldCheck,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { NavLink } from "react-router";

import { useAuth } from "@/auth/auth-context";

interface SettingsSection {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Admin-only rows are absent for most breeders rather than disabled. */
  adminOnly?: boolean;
}

const SECTIONS: readonly SettingsSection[] = [
  { to: "/more/profile", label: "Breeder details", icon: UserRound },
  { to: "/more/locations", label: "Cages & rooms", icon: Boxes },
  { to: "/breeding/seasons", label: "Breeding seasons", icon: CalendarRange },
  { to: "/more/notifications", label: "Reminders", icon: BellRing },
  { to: "/more/security", label: "Security", icon: ShieldCheck },
  { to: "/admin/users", label: "People", icon: Users, adminOnly: true },
];

/**
 * The frame every settings screen sits in: the sections beside the panel.
 *
 * Each settings screen is its own route, and on a phone that is right — you go into one,
 * do the thing, come back. On a desktop the same arrangement means the list disappears
 * the moment you pick from it, and getting from Security to Breeder details is two
 * navigations through a screen you did not want.
 *
 * So the list stays, in a 228px column beside the panel. It is here rather than in the
 * app rail because it is this screen's own navigation, not the app's: the rail says which
 * of the five sections of BreeNova you are in, and it should go on saying "settings"
 * while you move between these.
 */
export function SettingsFrame({ children }: { children: ReactNode }) {
  return (
    <div className="px-5 pb-lg md:px-gutter-wide">
      <div
        className={cn(
          "md:grid md:items-start md:gap-lg",
          "md:grid-cols-[var(--container-subnav)_minmax(0,1fr)]",
        )}
      >
        <SettingsSections />

        {/* `min-w-0` or a wide table inside a panel pushes the grid column past its
            track and the sections list is squeezed to nothing. */}
        <div className="flex min-w-0 flex-col gap-lg">{children}</div>
      </div>
    </div>
  );
}

/**
 * The sections list. Phone screens reach these from the hub at `/more`, so it is drawn
 * only where it has a column of its own to stand in.
 */
function SettingsSections() {
  const { isAdmin } = useAuth();

  return (
    <Card className="hidden flex-col gap-xs p-md md:flex">
      {SECTIONS.filter((section) => !section.adminOnly || isAdmin).map((section) => (
        <NavLink
          key={section.to}
          to={section.to}
          className={({ isActive }) =>
            cn(
              "flex h-touch items-center gap-tight rounded-control px-md transition-colors",
              "text-tab",
              isActive
                ? "bg-surface-container text-on-surface"
                : cn(
                    "font-semibold text-on-surface-body",
                    "hover:bg-surface-container-low hover:text-on-surface",
                  ),
            )
          }
        >
          {({ isActive }) => (
            <>
              <section.icon
                className={cn("size-4.5 shrink-0", isActive ? "text-primary" : null)}
                aria-hidden
              />
              <span className="min-w-0 truncate">{section.label}</span>
            </>
          )}
        </NavLink>
      ))}
    </Card>
  );
}
